/**
 * Match-kind + relevance helpers for GET /events/:interest results.
 *
 * Orthogonal axes (do not conflate):
 * - matchKind: why included (`direct` | `related`)
 * - relevance: how well text matches (`official_channel` > `title` > `channel` > `weak`)
 *
 * Missing matchKind is treated as "direct" for stale/fallback responses.
 */

const RELEVANCE_RANK = {
  official_channel: 0,
  title: 1,
  channel: 2,
  weak: 3,
};

export const MAX_RELATED_TOPICS = 3;

export function getMatchKind(event) {
  return event?.matchKind === 'related' ? 'related' : 'direct';
}

export function getRelevance(event) {
  const value = event?.relevance;
  if (
    value === 'official_channel' ||
    value === 'title' ||
    value === 'channel' ||
    value === 'weak'
  ) {
    return value;
  }
  return null;
}

export function isOfficialChannelEvent(event) {
  return (
    event?.officialChannel === true ||
    getRelevance(event) === 'official_channel'
  );
}

function relatedEntityName(event) {
  const name = event?.relatedEntity;
  return typeof name === 'string' ? name.trim() : '';
}

function eventStartMs(event) {
  const raw = event?.startsAt || event?.start;
  if (raw == null || raw === '') return Number.POSITIVE_INFINITY;
  const ms = Date.parse(raw);
  return Number.isNaN(ms) ? Number.POSITIVE_INFINITY : ms;
}

function relevanceRank(event) {
  if (isOfficialChannelEvent(event)) return RELEVANCE_RANK.official_channel;
  const relevance = getRelevance(event);
  if (relevance == null) return RELEVANCE_RANK.channel;
  return RELEVANCE_RANK[relevance] ?? RELEVANCE_RANK.channel;
}

function compareEvents(a, b) {
  const rankDiff = relevanceRank(a) - relevanceRank(b);
  if (rankDiff !== 0) return rankDiff;
  return eventStartMs(a) - eventStartMs(b);
}

function sortByRelevance(events) {
  return [...(events || [])].sort(compareEvents);
}

function mostCommonKey(counts) {
  let best = null;
  let bestCount = 0;
  for (const [key, count] of counts) {
    if (count > bestCount) {
      best = key;
      bestCount = count;
    }
  }
  return best;
}

function mostCommonRelatedEntity(relatedEvents) {
  const counts = new Map();
  for (const event of relatedEvents || []) {
    const name = relatedEntityName(event);
    if (!name) continue;
    counts.set(name, (counts.get(name) || 0) + 1);
  }
  return mostCommonKey(counts);
}

function mostCommonRelationshipType(relatedEvents, relatedEntity) {
  const counts = new Map();
  for (const event of relatedEvents || []) {
    if (relatedEntity && relatedEntityName(event) !== relatedEntity) continue;
    const type = event?.relationshipType;
    if (!type || typeof type !== 'string') continue;
    counts.set(type, (counts.get(type) || 0) + 1);
  }
  return mostCommonKey(counts);
}

/**
 * Partition by matchKind, drop weak noise in every topic, and group related
 * events by relatedEntity (capped) so the gallery can render one topic each.
 */
export function partitionByMatchKind(results) {
  const direct = [];
  const relatedGroups = new Map();

  for (const event of results || []) {
    if (getRelevance(event) === 'weak') continue;

    if (getMatchKind(event) === 'related') {
      const name = relatedEntityName(event);
      if (!name) continue;
      let group = relatedGroups.get(name);
      if (!group) {
        group = { relatedEntity: name, typeCounts: new Map(), events: [] };
        relatedGroups.set(name, group);
      }
      group.events.push(event);
      const type = event?.relationshipType;
      if (type && typeof type === 'string') {
        group.typeCounts.set(type, (group.typeCounts.get(type) || 0) + 1);
      }
    } else {
      direct.push(event);
    }
  }

  const relatedByEntity = [...relatedGroups.values()]
    .map((group) => ({
      relatedEntity: group.relatedEntity,
      relationshipType: mostCommonKey(group.typeCounts),
      events: sortByRelevance(group.events),
    }))
    .sort((a, b) => {
      const countDiff = b.events.length - a.events.length;
      if (countDiff !== 0) return countDiff;
      return a.relatedEntity.localeCompare(b.relatedEntity);
    })
    .slice(0, MAX_RELATED_TOPICS);

  return {
    direct: sortByRelevance(direct),
    relatedByEntity,
  };
}

/**
 * Secondary topic header for related events.
 * Prefers the mode relatedEntity + relationshipType copy table.
 */
export function relatedSectionLabel(relatedEvents, interest) {
  const relatedEntity = mostCommonRelatedEntity(relatedEvents);
  if (!relatedEntity) {
    return interest ? `Related to ${interest}` : 'Related events';
  }

  const relationshipType = mostCommonRelationshipType(relatedEvents, relatedEntity);
  switch (relationshipType) {
    case 'competes_in':
      return `Also in ${relatedEntity}`;
    case 'member_of':
    case 'has_part':
      return `Also from ${relatedEntity}`;
    case 'part_of':
      return `Part of ${relatedEntity}`;
    default:
      return `Related to ${relatedEntity}`;
  }
}

/**
 * Split a result bucket into live EPG / upcoming EPG / YouTube / Twitch / Kick.
 * Each list keeps relevance rank, then start time.
 */
export function groupByWatchSurface(events, now = new Date()) {
  const list = events || [];
  const liveEPG = [];
  const upcomingEPG = [];
  const youtube = [];
  const twitch = [];
  const kick = [];

  for (const event of list) {
    if (event.platform === 'youtube') {
      youtube.push(event);
      continue;
    }
    if (event.platform === 'twitch') {
      twitch.push(event);
      continue;
    }
    if (event.platform === 'kick') {
      kick.push(event);
      continue;
    }
    if (event.platform === 'concert') {
      const start = new Date(event.start);
      if (!Number.isNaN(start.getTime()) && start > now) upcomingEPG.push(event);
      continue;
    }
    if (event.platform === 'EPG') {
      const start = new Date(event.start);
      const end = new Date(event.end);
      if (start <= now && end >= now) liveEPG.push(event);
      else if (start > now) upcomingEPG.push(event);
    }
  }

  liveEPG.sort(compareEvents);
  upcomingEPG.sort(compareEvents);
  youtube.sort(compareEvents);
  twitch.sort(compareEvents);
  kick.sort(compareEvents);
  return { liveEPG, upcomingEPG, youtube, twitch, kick };
}
